/* eslint-disable import/no-cycle */
import pick from 'lodash/pick';
import get from 'lodash/get';
import template from 'lodash/template';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import { createACHMap, isValidAba, modifyACHMap } from '../../../../api/payments/service';
import length from '../../../../utils/validations/length';
import {
  FIXED_FORMAT_HEADER_FIELDS,
  getFixedFormatTableFields,
  MAP_FORMAT
} from './utils.mapFormats';
import { getDefaultInitialData } from './reducer.storeFormData';
import {
  API_DETAIL_FIELDS_VOCABULARY,
  API_TRAN_TYPE_ROW_VOCABULARY,
  DEFAULT_END_OF_RECORD_OPTION,
  DROPDOWN_OPTION_NONE,
  getTableFields,
  HEADER_FIELDS,
  NONE,
  TABLE_FIELDS,
  TABLE_FIELDS_NAMES,
  TRAN_TYPES_DATA,
  TRANSACTION_FIELD_VARIANT
} from './constants';
import { ADDENDA_MAX_AMT } from '../../../../constants';
import { validate } from '../../../../utils/validations/gluValidations';
import { PROCESS_MODES_NAMES } from '../utils';

export const transformDataToDropdown = {
  transactionCodes: (data) =>
    data.map((item) => ({ id: item.code, name: `${item.code} - ${item.name}` })),
  achDelimiters: (data) =>
    data.map((item) => ({ id: item.code, name: `${item.value} ( ${item.code} )` })),
  endOfRecords: (data) =>
    data.map((item) => ({
      id: item.code,
      name:
        item.code === 'NL'
          ? DEFAULT_END_OF_RECORD_OPTION[0].name
          : `${item.value} ( ${item.code} )`
    }))
};

export const startingPositionGreaterRecordLengthValidation = (locale) => ({
  fn: (name, _, data) => {
    if (!data[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]) {
      return true;
    }

    return Number(data[name]) > data[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]
      ? locale['wireImports.startingPositionIsGreaterThanRecordLength']
      : true;
  }
});

export const endingPositionGreaterRecordLengthValidation = (locale) => ({
  fn: (name, _, data) => {
    if (!data[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]) {
      return true;
    }

    const fieldName = name?.split('_')?.[0];
    const endingPosition = Number(data[name]) + Number(data[`${fieldName}_startPosition`]) - 1;

    return endingPosition > data[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]
      ? locale['wireImports.endingPositionIsGreaterThanRecordLength']
      : true;
  }
});

const calculateEndPosition = (fieldName, data) => {
  if (!data[`${fieldName}_startPosition`] || !data[`${fieldName}_length`]) return NaN;

  return Number(data[`${fieldName}_startPosition`]) + Number(data[`${fieldName}_length`]) - 1;
};

const createOverlapError = (locale, errorA, errorB) =>
  template(locale['wireImports.fieldAOverlapsFieldB'])({
    fieldA: errorA,
    fieldB: errorB
  });

export const overlapValidation = (locale) => ({
  // eslint-disable-next-line consistent-return
  fn: (name, _, data) => {
    const fieldSplitted = name?.split('_');
    const currentFieldName = fieldSplitted[0];
    const currentFieldParameter = name?.split('_').pop();
    const TABLE_FIELD_NAMES = TABLE_FIELDS_NAMES(locale);

    const fixedFormatTableFields = getFixedFormatTableFields(locale, true, true);

    const dataFromTableFields = pick(data, fixedFormatTableFields);

    const fixedFormatRanges = getTableFields(locale)
      .map((tableField) => ({
        name: tableField.id,
        startPosition: Number(dataFromTableFields[`${tableField.id}_startPosition`]),
        endPosition: calculateEndPosition(tableField.id, dataFromTableFields)
      }))
      .filter((fixedFormatField) => fixedFormatField.name !== currentFieldName); // delete currentField because it always fails validation with itself

    const startPosition = Number(data[`${currentFieldName}_startPosition`]);
    const endPosition = calculateEndPosition(currentFieldName, data);

    const startPositionFailed = fixedFormatRanges.find(
      (range) => startPosition >= range.startPosition && startPosition <= range.endPosition
    );
    const endPositionFailed = fixedFormatRanges.find(
      (range) => endPosition >= range.startPosition && endPosition <= range.endPosition
    );
    const firstMetRangeBeforeEndFailed = fixedFormatRanges.find(
      (range) =>
        range.startPosition > startPosition &&
        range.endPosition > startPosition &&
        range.startPosition < endPosition &&
        range.endPosition < endPosition
    );

    const failedLengthValidation = firstMetRangeBeforeEndFailed || endPositionFailed;

    if (currentFieldParameter === 'startPosition') {
      return startPositionFailed
        ? createOverlapError(
            locale,
            TABLE_FIELD_NAMES[currentFieldName],
            TABLE_FIELD_NAMES[startPositionFailed.name]
          )
        : true;
    }

    if (currentFieldParameter === 'length') {
      return failedLengthValidation
        ? createOverlapError(
            locale,
            TABLE_FIELD_NAMES[currentFieldName],
            TABLE_FIELD_NAMES[failedLengthValidation.name]
          )
        : true;
    }
  }
});

const sanitizeAmount = (value) => {
  if (!value) {
    return '';
  }
  const valueToSanitize = String(value);
  return valueToSanitize.replace(/,/g, '');
};

export const isAmountAddValueFieldEmpty = (formState) =>
  !parseFloat(sanitizeAmount(formState[`${TABLE_FIELDS.AMOUNT}_addValue`]));

export const requiredValidator = (locale, overrideValidationMessage) => ({
  fn: (name, _, data) => {
    const validationMessage = overrideValidationMessage || locale['validation.required'];

    return !data[name] ||
      Boolean(name === `${TABLE_FIELDS.AMOUNT}_addValue` && isAmountAddValueFieldEmpty(data))
      ? validationMessage
      : true;
  }
});

export const requiredTransactionCodeValidator = (locale) => [
  {
    fn: (name, _, data) =>
      !get(data[name], '[0].id') || get(data[name], '[0].id') === NONE
        ? locale['validation.required']
        : true
  }
];

export const asyncValidateBankCode = (maxLengthIndicatorLimit) => async (name, _, data) => {
  if (!data[name]) return true;

  if (maxLengthIndicatorLimit && data[name]?.length > maxLengthIndicatorLimit) return true;

  const { success } = await isValidAba({ abaNumber: data[name] }).toPromise();
  return success;
};

export const asyncExecuteMultiplyGluFunctions = async (arrayOfFieldNames, asyncGluFunc) => {
  /* eslint-disable no-restricted-syntax */
  /* eslint-disable no-await-in-loop */
  for (const fieldName of arrayOfFieldNames) {
    await asyncGluFunc(fieldName);
  }
};

export const asyncRegisterMultiplyGluValidators = async (arrayOfFields, registerValidators) => {
  /* eslint-disable no-restricted-syntax */
  /* eslint-disable no-await-in-loop */
  for (const field of arrayOfFields) {
    await registerValidators(field.name, field.validator);
  }
};

export const validateDuplicatePositionField = (locale) => (name, _, data) => {
  const pickFieldsData = pick(
    data,
    getTableFields(locale)
      .map((item) => (item.id !== name ? item.id : null))
      .filter(Boolean)
  );
  const hasDuplicatePosition = Object.keys(pickFieldsData).some(
    (item) => Number(data[item]) === Number(data[name])
  );

  if (data[name] && hasDuplicatePosition) {
    // data[name] for CB-39613
    return locale['wireImports.duplicateField'];
  }
  return true;
};

export const validateLength = (maxLengthIndicatorLimit, locale) => ({
  fn: (name, _, data) =>
    length(data[name], {
      max: maxLengthIndicatorLimit,
      locale: {
        length: template(locale['common.maxCharacters'])({
          max: maxLengthIndicatorLimit
        })
      }
    })
});

export const validateAmountFieldLength = (locale) =>
  validate({
    validate: (val) => {
      if (val && typeof val.valueOf() === 'string') {
        const value = parseFloat(val.replace(/[^.\d]/g, ''));
        if (!Number.isNaN(value)) return value <= ADDENDA_MAX_AMT;
      }
      return true;
    },
    msg: locale['limits.default']
  });

export const validateRangeLimitation = (locale, limitation) => ({
  fn: (name, _, data) =>
    data[name] && data[name] > limitation ? locale['wireImports.canNotBeGrater15'] : true
});

export const validateGraterThanOne = (locale) => ({
  fn: (name, _, data) =>
    data[name] && data[name] < 1 ? locale['wireImports.mustBeGraterThanOne'] : true
});

export const validateGraterThanZero = (locale) => ({
  fn: (name, _, data) =>
    data[name] && data[name] < 0 ? locale['wireImports.mustBeGraterThanZero'] : true
});

export const delimitedDropdownValidation = (locale, validationMessage) => [
  { required: true, msg: locale['validation.required'] },
  {
    fn: /* istanbul ignore next */ (...args) => {
      const [, , data] = args;
      const delimiter = get(data, 'delimiter[0]', {});
      const endOfRecordMethod = get(data, 'endOfRecordMethod[0]', {});

      const sameEndOfRecordWithDelimiter =
        delimiter.id === endOfRecordMethod.id && delimiter.name === endOfRecordMethod.name;

      if (sameEndOfRecordWithDelimiter) {
        return locale[`wireImports.${validationMessage}`];
      }
      return true;
    }
  }
];

export const validateUnique = (locale, inputs) => (name, _, data) => {
  if (!data[name]) {
    return true;
  }

  const currentField = data[name];
  const hasDuplicate = inputs.find((input) => input !== name && currentField === data[input]);

  if (hasDuplicate) return locale['wireImports.uniqueTranTypePerMap'];

  return true;
};

// UPDATE_ONLY

const createFieldNamesChainString = (fieldNames, locale) => {
  const TABLE_FIELD_NAME = TABLE_FIELDS_NAMES(locale);

  return fieldNames?.length === 1
    ? TABLE_FIELD_NAME[fieldNames[0]]
    : fieldNames?.map((field) => TABLE_FIELD_NAME[field]).join(', ');
};

const UPDATE_FORM_ERRORS = (locale) => ({
  AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED: {
    code: 1,
    message: locale['wireImports.atLeastOneUpdateSelectedError']
  },
  AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED: {
    code: 2,
    message: locale['wireImports.atLeastOneMatchSelectedError']
  },
  AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED: {
    code: 3,
    message: locale['wireImports.atLeastOneMatchOrUpdateSelectedError']
  },
  REQUIRES_POSITION_OR_ADD_VALUE: (fieldNames) => ({
    code: 4,
    message: template(locale['wireImports.matchRequiresPositionOrAddValueError'])({
      fieldNames: createFieldNamesChainString(fieldNames, locale),
      ending: fieldNames?.length === 1 ? '' : 's'
    })
  })
});

const createFormError = (errorToSet, setFormErrorsFunc) => {
  setFormErrorsFunc((prevState) => {
    const errorAlreadyExists = prevState.find((error) => error.code === errorToSet.code);

    if (errorAlreadyExists) {
      return prevState;
    }

    return [...prevState, errorToSet];
  });
};

export const clearFormError = (code, setFormErrorsFunc) =>
  setFormErrorsFunc((prevState) => prevState.filter((error) => error.code !== code));

export const atLeastOneUpdateAndMatchValidation = (formState, setFormErrorsFunc, locale) => {
  const {
    AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED,
    AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED,
    AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED
  } = UPDATE_FORM_ERRORS(locale);

  const selectedUpdateCheckboxes = pickBy(
    formState,
    (value, key) => key.includes('_update') && value
  );
  const selectedMatchCheckboxes = pickBy(
    formState,
    (value, key) => key.includes('_match') && value
  );

  const atLeastOneUpdateSelected = Object.keys(selectedUpdateCheckboxes).length > 0;
  const atLeastOneMatchSelected = Object.keys(selectedMatchCheckboxes).length > 0;

  if (!atLeastOneUpdateSelected && !atLeastOneMatchSelected) {
    clearFormError(AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED.code, setFormErrorsFunc);
    clearFormError(AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);
    createFormError(AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED, setFormErrorsFunc);

    return false; // Form fails validation with message
  }

  if (!atLeastOneUpdateSelected) {
    createFormError(AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED, setFormErrorsFunc);
    clearFormError(AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED.code, setFormErrorsFunc);
    clearFormError(AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);

    return false; // Form fails validation with message
  }

  if (!atLeastOneMatchSelected) {
    createFormError(AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED, setFormErrorsFunc);
    clearFormError(AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);
    clearFormError(AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);

    return false; // Form fails validation with message
  }

  clearFormError(AT_LEAST_ONE_MATCH_CHECKBOX_SELECTED.code, setFormErrorsFunc);
  clearFormError(AT_LEAST_ONE_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);
  clearFormError(AT_LEAST_ONE_MATCH_OR_UPDATE_CHECKBOX_SELECTED.code, setFormErrorsFunc);

  return true; // Form passes validation
};

const createMatchError = (errorToSet, setFormErrorsFunc) =>
  setFormErrorsFunc((prevState) => {
    const errors = prevState.filter((error) => error.code !== errorToSet.code);

    return [...errors, errorToSet];
  });

const checkPositionFieldValueIsAvailable = ({ accountTypesData, field, formState, mapFormat }) => {
  if (field === TABLE_FIELDS.TRAN_TYPE || field === TABLE_FIELDS.ACCOUNT_TYPE) {
    const transactionTypePosition =
      mapFormat === MAP_FORMAT.DELIMITED
        ? TABLE_FIELDS.TRAN_TYPE
        : `${TABLE_FIELDS.TRAN_TYPE}_startPosition`;

    const accountTypePosition =
      mapFormat === MAP_FORMAT.DELIMITED
        ? TABLE_FIELDS.ACCOUNT_TYPE
        : `${TABLE_FIELDS.ACCOUNT_TYPE}_startPosition`;

    const hasTransactionTypeValues = TRAN_TYPES_DATA.find((item) => formState[item.id]);
    const hasAccountTypeValues = accountTypesData.find((item) => formState[item.id]);

    if (mapFormat === MAP_FORMAT.FIXED) {
      return field === TABLE_FIELDS.TRAN_TYPE
        ? formState[transactionTypePosition] || hasTransactionTypeValues
        : formState[accountTypePosition] || hasAccountTypeValues;
    }

    return field === TABLE_FIELDS.TRAN_TYPE
      ? formState[transactionTypePosition] && hasTransactionTypeValues
      : formState[accountTypePosition] && hasAccountTypeValues;
  }

  return mapFormat === MAP_FORMAT.DELIMITED
    ? formState[field]
    : formState[`${field}_startPosition`] && formState[`${field}_length`];
};

const validateCheckbox = (name, formState, mapFormat, accountTypesData) => {
  const checkboxField = `_${name}`;

  const selectedMatchCheckboxes = pickBy(
    formState,
    (value, key) => key.includes(checkboxField) && value
  );

  if (Object.keys(selectedMatchCheckboxes).length) {
    const selectedMatchFields = Object.keys(selectedMatchCheckboxes).map((item) =>
      item.replace(checkboxField, '')
    );

    // get all fields where both POSITION and ADD_VALUE are not selected
    const failedMatchFields = selectedMatchFields.filter((field) => {
      // TRAN_TPE && ACCOUNT_TYPE
      if (field === TABLE_FIELDS.TRAN_TYPE || field === TABLE_FIELDS.ACCOUNT_TYPE) {
        return (
          !checkPositionFieldValueIsAvailable({ field, accountTypesData, formState, mapFormat }) &&
          get(formState[`${field}_addValue`], '[0].id') === NONE
        );
      }
      // TRAN_CODE has NONE as default
      if (field === TABLE_FIELDS.TRAN_CODE) {
        return (
          !checkPositionFieldValueIsAvailable({ field, accountTypesData, formState, mapFormat }) &&
          get(formState[`${field}_addValue`], '[0].id') === NONE
        );
      }

      // ID, ADDENDA - have no ADD_VALUE
      if (field === TABLE_FIELDS.ID || field === TABLE_FIELDS.ADDENDA) {
        return !checkPositionFieldValueIsAvailable({
          field,
          accountTypesData,
          formState,
          mapFormat
        });
      }

      return (
        !checkPositionFieldValueIsAvailable({ field, accountTypesData, formState, mapFormat }) &&
        !formState[`${field}_addValue`]
      );
    });

    if (failedMatchFields.length) {
      // FAILED
      return failedMatchFields;
    }

    // PASSED
    return [];
  }

  // NO MATCH CHECKBOX SELECTED - PASSED
  return [];
};

export const validateMatchAndUpdateCheckboxes = (
  formState,
  setFormErrorsFunc,
  locale,
  mapFormat,
  accountTypesData
) => {
  const { REQUIRES_POSITION_OR_ADD_VALUE } = UPDATE_FORM_ERRORS(locale);

  const validateMatch = validateCheckbox('match', formState, mapFormat, accountTypesData);
  const validateUpdate = validateCheckbox('update', formState, mapFormat, accountTypesData);

  if (validateMatch.length || validateUpdate.length) {
    createMatchError(
      REQUIRES_POSITION_OR_ADD_VALUE([...validateMatch, ...validateUpdate]),
      setFormErrorsFunc
    );
    return false;
  }

  clearFormError(REQUIRES_POSITION_OR_ADD_VALUE([]).code, setFormErrorsFunc);
  return true;
};

// API

const extractTanCodeOrEndOfRecordForAPI = (tranCode) => {
  if (!tranCode) return null;

  if (tranCode === 'NEW LINE') {
    return 'NEWLINE'; // API format
  }
  return tranCode.split(' ')[0];
};

export const sendACHMap = ({ data, mapCode, mapFormat, modify }) =>
  modify
    ? modifyACHMap(
        {
          ...data,
          achImportMapHeader: {
            ...data.achImportMapHeader,
            mapCode
          }
        },
        mapFormat
      )
    : createACHMap(data, mapFormat);

const getReplacementValueForAPI = (item, data, fieldAddValue) => {
  const searchPath = item === TABLE_FIELDS.TRAN_CODE ? '[0].id' : '[0].name';

  if (
    (item === TABLE_FIELDS.TRAN_CODE ||
      item === TABLE_FIELDS.TRAN_TYPE ||
      item === TABLE_FIELDS.ACCOUNT_TYPE) &&
    data[`${item}_addValue`]
  ) {
    const dropdownAddValue = get(data[`${item}_addValue`], '[0].id', null);

    if (dropdownAddValue === NONE || !dropdownAddValue) {
      return null;
    }

    return String(get(data[`${item}_addValue`], searchPath, ''));
  }

  return fieldAddValue;
};

const getMapTypePositionForFormAPI = ({ data, fieldPosition, item, mapFormat }) =>
  mapFormat === MAP_FORMAT.DELIMITED
    ? {
        fieldPosNumber: fieldPosition
      }
    : {
        startPosition: Number(data[`${item}_startPosition`]) || null,
        fieldLength: Number(data[`${item}_length`]) || null
      };

export const formAPIData = ({ data, mapFormat, processMode, transactionFieldVariant }) => {
  const achImportMapDetails = Object.values(TABLE_FIELDS)
    // This filter is for transactionFieldVariant to delete hidden fields
    .filter((field) => {
      if (transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRANSACTION_CODE) {
        return ![TABLE_FIELDS.TRAN_TYPE, TABLE_FIELDS.ACCOUNT_TYPE].includes(field);
      }

      if (transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE) {
        return ![TABLE_FIELDS.TRAN_CODE].includes(field);
      }
      return null;
    })
    .map((item) => {
      if (mapFormat === MAP_FORMAT.DELIMITED && !data[item] && !data[`${item}_addValue`])
        return false; // field is OPTIONAL and not selected
      if (
        mapFormat === MAP_FORMAT.FIXED &&
        !data[`${item}_startPosition`] &&
        !data[`${item}_length`] &&
        !data[`${item}_addValue`]
      )
        return false; // field is OPTIONAL and not selected

      const fieldPosition = Number(data[item]) || null;
      const fieldAddValue = data[`${item}_addValue`] ? String(data[`${item}_addValue`]) : null;

      const mapTypePositionFields = getMapTypePositionForFormAPI({
        item,
        data,
        mapFormat,
        fieldPosition
      });

      if (
        item === TABLE_FIELDS.AMOUNT &&
        (data[item] || data[`${TABLE_FIELDS.AMOUNT}_addValue`])
      ) {
        return {
          ...mapTypePositionFields,
          fieldName: API_DETAIL_FIELDS_VOCABULARY[item],
          amountDecimal:
            fieldAddValue && !isAmountAddValueFieldEmpty(data)
              ? parseFloat(sanitizeAmount(fieldAddValue))
              : null, // Here check if amount is 0 - does not send it, send null
          replacementValue: null,
          // UPDATE_ONLY AND ADD_UPDATE processModes
          ...(data[`${item}_match`] ? { matchFlag: data[`${item}_match`] } : {}),
          ...(data[`${item}_update`] ? { updateFlag: data[`${item}_update`] } : {})
        };
      }

      return {
        ...mapTypePositionFields,
        fieldName: API_DETAIL_FIELDS_VOCABULARY[item],
        amountDecimal: null,
        replacementValue: getReplacementValueForAPI(item, data, fieldAddValue),
        // UPDATE_ONLY AND ADD_UPDATE processModes
        ...(data[`${item}_match`] ? { matchFlag: data[`${item}_match`] } : {}),
        ...(data[`${item}_update`] ? { updateFlag: data[`${item}_update`] } : {}),
        // transactionFieldVariant === TRANSACTION_TYPE
        ...(transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE &&
        item === TABLE_FIELDS.TRAN_TYPE
          ? {
              ...(data?.credit ? { creditType: data?.credit } : {}),
              ...(data?.debit ? { debitType: data?.debit } : {}),
              ...(data?.prenoteDebit ? { prenoteDebit: data?.prenoteDebit } : {}),
              ...(data?.prenoteCredit ? { prenoteCredit: data?.prenoteCredit } : {})
            }
          : {}),
        ...(transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE &&
        item === TABLE_FIELDS.ACCOUNT_TYPE
          ? {
              ...(data?.checking ? { checkingType: data?.checking } : {}),
              ...(data?.saving ? { savingType: data?.saving } : {}),
              ...(data?.loan ? { loanType: data?.loan } : {}),
              ...(data?.generalLedger ? { glType: data?.generalLedger } : {})
            }
          : {})
      };
    })
    .filter(Boolean);

  return {
    achImportMapHeader: {
      processMode,
      mapName: data.mapName,
      ...(mapFormat === MAP_FORMAT.DELIMITED
        ? {
            [HEADER_FIELDS.DELIMITER]: extractTanCodeOrEndOfRecordForAPI(
              get(data.delimiter, '[0].name', null)
            ),
            [HEADER_FIELDS.END_OF_RECORD_METHOD]: extractTanCodeOrEndOfRecordForAPI(
              get(data[HEADER_FIELDS.END_OF_RECORD_METHOD], '[0].name', null)
            )
          }
        : {
            [FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]:
              Number(data[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]) || null
          }),
      [HEADER_FIELDS.SKIP_FIRST_RECORD]: data[HEADER_FIELDS.SKIP_FIRST_RECORD],
      ...(mapFormat === MAP_FORMAT.DELIMITED
        ? { [HEADER_FIELDS.STRIP_QUOTES]: data[HEADER_FIELDS.STRIP_QUOTES] }
        : {}),
      [HEADER_FIELDS.ZERO_AMOUNT_FIELDS]: data[HEADER_FIELDS.ZERO_AMOUNT_FIELDS]
    },
    achImportMapDetails
  };
};

const getReplacementValueFromAPI = ({ accountTypesData, field, transactionCodesData }) => {
  if (field.fieldName === API_DETAIL_FIELDS_VOCABULARY.amount) {
    return field.amountDecimal;
  }

  if (field.fieldName === API_DETAIL_FIELDS_VOCABULARY.transactionCode) {
    return transactionCodesData.filter((item) =>
      String(item.name).includes(field.replacementValue)
    );
  }

  if (field.fieldName === API_DETAIL_FIELDS_VOCABULARY.transactionType) {
    return TRAN_TYPES_DATA.filter((item) => item.name === field.replacementValue);
  }

  if (field.fieldName === API_DETAIL_FIELDS_VOCABULARY.accountType) {
    return accountTypesData.filter((item) => item.name === field.replacementValue);
  }

  return field.replacementValue;
};

export const getImportFileInputsForField = (field) =>
  Object.keys(field).reduce((accumulator, key) => {
    if (API_TRAN_TYPE_ROW_VOCABULARY[key] && field[key]) {
      return {
        ...accumulator,
        [API_TRAN_TYPE_ROW_VOCABULARY[key]]: field[key]
      };
    }

    return accumulator;
  }, {});

export const formInitialStateFromApiData = ({
  accountTypesData,
  achDelimitersData,
  achImportMapData,
  endOfRecordsData,
  mapFormat,
  transactionCodesData
}) => {
  const defaultInitialData = getDefaultInitialData(mapFormat);

  if (!achImportMapData) return defaultInitialData;

  const { achImportMapDetails, achImportMapHeader } = achImportMapData;

  const headerData = {
    [HEADER_FIELDS.MAP_NAME]: achImportMapHeader.mapName,
    [HEADER_FIELDS.SKIP_FIRST_RECORD]: achImportMapHeader[HEADER_FIELDS.SKIP_FIRST_RECORD],
    [HEADER_FIELDS.STRIP_QUOTES]: achImportMapHeader[HEADER_FIELDS.STRIP_QUOTES],
    [HEADER_FIELDS.ZERO_AMOUNT_FIELDS]: achImportMapHeader[HEADER_FIELDS.ZERO_AMOUNT_FIELDS],
    // DELIMITED
    [HEADER_FIELDS.DELIMITER]: achDelimitersData.filter(
      (item) => item.name.split(' ')[0] === achImportMapHeader[HEADER_FIELDS.DELIMITER]
    ), // format 'COMMA (,)', has to be split and only COMMA taken
    [HEADER_FIELDS.END_OF_RECORD_METHOD]: endOfRecordsData.filter((item) => {
      if (item.name === 'NEW LINE') {
        return (
          item.name.replace(' ', '') === achImportMapHeader[HEADER_FIELDS.END_OF_RECORD_METHOD]
        );
      }

      return item.name.split(' ')[0] === achImportMapHeader[HEADER_FIELDS.END_OF_RECORD_METHOD];
    }),
    // FIXED
    [FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]:
      achImportMapHeader[FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH]
  };

  const tableData = achImportMapDetails.reduce((accumulator, field) => {
    const importFileInputs = getImportFileInputsForField(field);

    return {
      ...accumulator,
      ...(field.replacementValue || field.amountDecimal
        ? {
            [`${TABLE_FIELDS[field.fieldName]}_addValue`]: getReplacementValueFromAPI({
              field,
              transactionCodesData,
              accountTypesData
            })
          }
        : {}),
      // UPDATE_ONLY OR ADD_AND_UPDATE
      ...(field.matchFlag ? { [`${TABLE_FIELDS[field.fieldName]}_match`]: field.matchFlag } : {}),
      ...(field.updateFlag
        ? { [`${TABLE_FIELDS[field.fieldName]}_update`]: field.updateFlag }
        : {}),
      // DELIMITED
      ...(field.fieldPosNumber ? { [TABLE_FIELDS[field.fieldName]]: field.fieldPosNumber } : {}),
      // FIXED
      ...(field.startPosition
        ? { [`${TABLE_FIELDS[field.fieldName]}_startPosition`]: field.startPosition }
        : {}),
      ...(field.fieldLength
        ? { [`${TABLE_FIELDS[field.fieldName]}_length`]: field.fieldLength }
        : {}),
      // ImportFile - for TransactionTypeRow
      ...(Object.keys(importFileInputs).length ? importFileInputs : {})
    };
  }, {});

  return {
    ...headerData,
    ...tableData
  };
};

export const castFieldsToNumber = (data) => {
  const nonNumericFields = [
    HEADER_FIELDS.MAP_NAME,
    HEADER_FIELDS.DELIMITER,
    HEADER_FIELDS.END_OF_RECORD_METHOD,
    `${TABLE_FIELDS.TRAN_CODE}_addValue`,
    `${TABLE_FIELDS.NAME}_addValue`
  ];

  const castedData = {};

  Object.keys(data).map((key) => {
    if (nonNumericFields.includes(key) || !Number(data[key])) {
      castedData[key] = data[key];
      return null;
    }
    castedData[key] = Number(data[key]);
    return null;
  });

  return castedData;
};

export const deleteEmptyField = (value) => {
  if (get(value, '[0].id') === NONE) {
    return false;
  }
  return identity(value);
};

export const clearComponent = ({
  field,
  importFileInputs = [],
  onChange,
  processMode,
  unregisterValidators
}) => {
  const componentFields = [
    ...importFileInputs,
    field.id,
    ...[`${field.id}_startPosition`, `${field.id}_length`],
    ...(processMode === PROCESS_MODES_NAMES.UPDATE_ONLY ||
    processMode === PROCESS_MODES_NAMES.ADD_AND_UPDATE
      ? [`${field.id}_match`, `${field.id}_update`]
      : [])
  ];

  const fieldsToUnmount = [...componentFields, `${field.id}_addValue`];

  componentFields.forEach((input) => onChange(input, undefined)); // all fields
  onChange(`${field.id}_addValue`, [DROPDOWN_OPTION_NONE]); // addValue

  return asyncExecuteMultiplyGluFunctions(fieldsToUnmount, unregisterValidators);
};
